/**
 * @module experience/shared-browse
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

// Core Angular
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

// Interfaces
import {
  EntryModalInterface,
  ModalState,
} from '@experience/shop/shared-browse/interfaces/entry-modal.interface';
import { BrowsePlanConfig } from '@core/aem/interfaces/shop/browse-plan.interface';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { APIError } from '@support/api/common/error.interface';
import { CartLine } from '@support/api/proxies/responses/api-cart-response.interface';
import { ProductDetails } from '@experience/shop/product-details/interfaces/product-details.interface';
import { Cart } from '@support/api/proxies/responses/api-cart-response.interface';

// Constants
import {
  SHARED_BROWSE_COMPONENTS,
  DEVICE_ERROR_MODAL,
} from '@experience/shop/shared-browse/constants/shared-browse.constants';

// Services
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { LoggerService } from '@core/logger/services/logger.service';
import { takeUntil } from 'rxjs/operators';
import { AddDeviceToNonLineItemsRequestModel } from 'app/model/add-nonline-device-request.model';

@Component({
  selector: 'tmo-device-error-modal',
  templateUrl: './device-error-modal.component.html',
  styleUrls: ['../../../../../../style-sheets/components/modal.component.scss'],
})
export class DeviceErrorModalComponent implements OnInit, OnDestroy {
  /**
   * Entrymodal input interface
   */
  @Input() public modal: EntryModalInterface;
  /**
   * Input.
   * @memberof DeviceErrorModalComponent
   */
  public displayModal: boolean;
  /**
   * Selected Device details
   * @memberof DeviceErrorModalComponent
   */
  @Input() public deviceDetail: ProductDetails;
  /**
   * Input. Stores Browse Plan AEM Data
   * @memberof DeviceErrorModalComponent
   */
  @Input() public aemConfigurableData: BrowsePlanConfig;

  /**
   * Input. Stores Product Offerings AEM Data
   * @memberof DeviceErrorModalComponent
   */
  @Input() public aemContent: ConfigData;
  /**
   * Cart List
   */
  public cartList: CartLine;

  private ngUnsubscribe: Subject<void>;

  constructor(
    private router: Router,
    private cartSupportService: CartSupportService,
    private stateUtilService: StateStoreUtilsService,
    private logger: LoggerService
  ) {
    this.displayModal = false;
    this.ngUnsubscribe = new Subject<void>();
  }
  /**
   * this method is executed on load.
   * @returns void
   */
  public ngOnInit(): void {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL,
      'entering ngOnInit() in ' + SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL
    );
    const cart = this.stateUtilService.getCart();
    if (cart) {
      this.cartList = cart.lines[0];
    }
    if (this.modal && this.modal.toggleModalEmitter) {
      this.modal.toggleModalEmitter
        .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
        .subscribe((event: ModalState) => {
          this.displayModal = event.open;
        });
    }
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL,
      'exiting ngOnInit() in ' + SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL
    );
  }

  /**
   * Unsubscribes for all subscriptions
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL,
      'ngOnDestroy() in ' + SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL
    );
  }

  /**
   * Method to add device in the cart
   * @param void
   * @returns void
   */
  public addToCart(): void {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL,
      'addToCart() in ' + SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL
    );
    const sku =
      this.deviceDetail[this.deviceDetail.defaultCondition][this.deviceDetail.defaultSelection]
        .skuCode;
    const skuItemCollection = [];
    const skuItem = new AddDeviceToNonLineItemsRequestModel();
    skuItem.setSkuDetails(sku);
    skuItemCollection.push(skuItem.getDeviceInfo());
    this.cartSupportService
      .postDeviceAsNonLineItem(skuItemCollection)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response: Cart) => {
        this.goToCart();
      });
  }
  /**
   * function gotoCart  to navigate to cart on click.
   * @param void
   * @returns void
   */
  public goToCart(): void {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL,
      'gotToCart() in ' + SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL
    );
    this.router.navigate([DEVICE_ERROR_MODAL.CART]);
  }
  /**
   * Mehtod for cart selection
   * @public
   * @memberof newSelection
   * returns void
   */
  public newSelection(): void {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL,
      'entering newSelection() in ' + SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL
    );
    this.cartSupportService
      .deleteCart()
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        () => {
          this.router.navigate([DEVICE_ERROR_MODAL.PRODUCT_SELECTOR]);
        },
        (err: APIError) => {
          this.displayModal = false;
        }
      );
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL,
      'exiting newSelection() in ' + SHARED_BROWSE_COMPONENTS.DEVICE_ERROR_MODAL
    );
  }
}
